import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Touch-friendly button component with:
 * - Minimum 44x44px tap targets (WCAG 2.5.5)
 * - Touch ripple effects for visual feedback
 * - Debounced touch handling to prevent double-taps
 * - Active state visual feedback
 */

const buttonVariants = cva(
  // Base styles with touch-friendly enhancements
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium " +
  // Focus states
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 " +
  // Disabled states
  "disabled:pointer-events-none disabled:opacity-50 " +
  // SVG handling
  "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 " +
  // Elevation system
  "hover-elevate active-elevate-2 " +
  // Touch-friendly: prevent text selection, use manipulation for faster taps
  "select-none touch-action-manipulation " +
  // Touch active state with scale feedback
  "active:scale-[0.98] transition-transform duration-100 " +
  // Ripple container
  "relative overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground border border-primary-border",
        destructive:
          "bg-destructive text-destructive-foreground border border-destructive-border",
        outline:
          // Shows the background color of whatever card / sidebar / accent background it is inside of.
          // Inherits the current text color.
          " border [border-color:var(--button-outline)]  shadow-xs active:shadow-none ",
        secondary: "border bg-secondary text-secondary-foreground border border-secondary-border ",
        // Add a transparent border so that when someone toggles a border on later, it doesn't shift layout/size.
        ghost: "border border-transparent",
      },
      // Heights are set as "min" heights, because sometimes Ai will place large amount of content
      // inside buttons. With a min-height they will look appropriate with small amounts of content,
      // but will expand to fit large amounts of content.
      // Updated to meet 44px minimum touch target requirement
      size: {
        default: "min-h-11 px-4 py-2", // 44px min height for touch
        sm: "min-h-10 rounded-md px-3 text-xs", // 40px, still touch-friendly
        lg: "min-h-12 rounded-md px-8", // 48px for larger touch target
        icon: "h-11 w-11", // 44x44px touch target
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Enable ripple effect on touch/click (default: true) */
  ripple?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ripple = true, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const [ripples, setRipples] = React.useState<Array<{ x: number; y: number; id: number }>>([])
    const rippleIdRef = React.useRef(0)
    const lastClickRef = React.useRef(0)

    // Debounce delay to prevent double-taps (300ms)
    const DEBOUNCE_MS = 300

    const handleInteraction = React.useCallback((
      e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>,
      clientX: number,
      clientY: number
    ) => {
      if (!ripple || props.disabled) return

      const button = e.currentTarget
      const rect = button.getBoundingClientRect()
      const x = clientX - rect.left
      const y = clientY - rect.top
      const id = ++rippleIdRef.current

      setRipples(prev => [...prev, { x, y, id }])

      // Remove ripple after animation
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== id))
      }, 500)
    }, [ripple, props.disabled])

    const handleClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
      // Debounce check
      const now = Date.now()
      if (now - lastClickRef.current < DEBOUNCE_MS) {
        e.preventDefault()
        return
      }
      lastClickRef.current = now

      // Trigger ripple
      handleInteraction(e, e.clientX, e.clientY)

      // Call original onClick
      onClick?.(e)
    }, [handleInteraction, onClick])

    const handleTouchStart = React.useCallback((e: React.TouchEvent<HTMLButtonElement>) => {
      const touch = e.touches[0]
      if (touch) {
        handleInteraction(e, touch.clientX, touch.clientY)
      }
    }, [handleInteraction])

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        {...props}
      >
        {/* Ripple elements */}
        {ripple && ripples.map(({ x, y, id }) => (
          <span
            key={id}
            className="touch-ripple"
            style={{
              left: x,
              top: y,
              width: 100,
              height: 100,
              marginLeft: -50,
              marginTop: -50,
            }}
          />
        ))}
        {props.children}
      </Comp>
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }
